import { CommonModule } from '@angular/common';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';

import { Popup } from '../../../../../core/shared/components/popup/popup';
import { ToastService } from '../../../../../core/shared/services/toast.service';
import { CraditService } from '../../services/cradit.service';
import { CdkDrag, CdkDragDrop, CdkDragHandle, CdkDropList } from '@angular/cdk/drag-drop';
import { RichTextEditor } from '../rich-text-editor/rich-text-editor';
import { SectionDragHandle } from '../section-drag-handle/section-drag-handle';
import { RichTextPipe } from '../../utils/rich-text.pipe';
import {
  bulletLinesToRichText,
  hasRichTextContent,
  paragraphBlockData,
  readParagraphBlockText,
  richTextForSave,
} from '../../utils/rich-text.util';
import { bySortOrder, moveVisibleItem } from '../../utils/section-reorder.util';

export interface RiskItem {
  /** Rich text from the editor (bullets are made with the editor's list buttons). */
  content: string;
  expanded?: boolean;
  crdtPrpslBlkSeq?: number | null;
  delFlg?: number;
}

@Component({
  selector: 'app-risk-section',
  imports: [
    CommonModule,
    FormsModule,
    Popup,
    TranslatePipe,
    RichTextEditor,
    RichTextPipe,
    CdkDropList,
    CdkDrag,
    CdkDragHandle,
    SectionDragHandle,
  ],
  templateUrl: './risk-section.html',
  styleUrl: './risk-section.css',
})
export class RiskSection implements OnInit, OnDestroy {
  @Input() items: RiskItem[] = [];
  @Input() conclusion = '';
  @Input() editable = false;

  onboardingId!: number;
  crdtPrpslSeq: number | null = null;
  conclusionBlkSeq: number | null = null;

  showDeletePopup = false;
  deleteMessage = '';
  private pendingDeleteIndex: number | null = null;
  private pendingDeleteType: 'risk' | 'conclusion' | null = null;

  private saveSub?: Subscription;
  private seqSub?: Subscription;

  constructor(
    private creditService: CraditService,
    private route: ActivatedRoute,
    private toast: ToastService,
    private translate: TranslateService,
  ) {}

  ngOnInit(): void {
    this.onboardingId = Number(
      this.route.snapshot.paramMap.get('id') ||
      this.route.parent?.snapshot.paramMap.get('oid')
    );

    // ✅ shared crdtPrpslSeq sync — jab bhi seq update ho, risks reload karo
    this.seqSub = this.creditService.crdtPrpslSeq$.subscribe((seq) => {
      this.crdtPrpslSeq = seq;
      this.loadRiskSection();
    });

    this.saveSub = this.creditService.saveSectionTrigger$.subscribe((sectionId) => {
      if (sectionId === 'key-risks') {
        this.saveRiskSection();
      }
    });
  }

  ngOnDestroy(): void {
    this.saveSub?.unsubscribe();
    this.seqSub?.unsubscribe();
  }

  get activeItems(): RiskItem[] {
    return this.items.filter(item => item.delFlg !== 1);
  }

  addRisk(): void {
    this.items.push({
      content: '',
      expanded: true,
      crdtPrpslBlkSeq: null,
      delFlg: 0,
    });
  }

  /** Drag-and-drop reorder; the new order is persisted via sortOrdr on save. */
  onRiskDrop(event: CdkDragDrop<unknown>): void {
    this.items = moveVisibleItem(
      this.items,
      (item) => item.delFlg !== 1,
      event.previousIndex,
      event.currentIndex,
    );
  }

  requestRemoveRisk(index: number): void {
    this.pendingDeleteType = 'risk';
    this.pendingDeleteIndex = index;
    this.deleteMessage = 'CREDIT_SECTION_RISK.DELETE_RISK_CONFIRM';
    this.showDeletePopup = true;
  }

  requestRemoveConclusion(): void {
    this.pendingDeleteType = 'conclusion';
    this.deleteMessage = 'CREDIT_SECTION_RISK.DELETE_CONCLUSION_CONFIRM';
    this.showDeletePopup = true;
  }

  confirmDelete(): void {
    if (this.pendingDeleteType === 'risk') {
      if (this.pendingDeleteIndex !== null) {
        this.removeRisk(this.pendingDeleteIndex);
        this.toast.success('CREDIT_SECTION_RISK.RISK_DELETED');
      }
    } else if (this.pendingDeleteType === 'conclusion') {
      this.conclusion = '';
      this.toast.success('CREDIT_SECTION_RISK.CONCLUSION_DELETED');
    }

    this.pendingDeleteIndex = null;
    this.pendingDeleteType = null;
  }

  private removeRisk(index: number): void {
    const selectedItem = this.activeItems[index];

    if (!selectedItem) return;

    if (selectedItem.crdtPrpslBlkSeq) {
      selectedItem.delFlg = 1;
    } else {
      this.items = this.items.filter(item => item !== selectedItem);
    }
  }

  loadRiskSection(): void {

    // ✅ jab tak shared crdtPrpslSeq na mile, kuch load nahi karna
    if (!this.crdtPrpslSeq) {
      this.items = [];
      this.conclusion = '';
      this.conclusionBlkSeq = null;
      return;
    }

    // ✅ getSections ki jagah getCrdtBlck use karo
    this.creditService.getCrdtBlck(this.crdtPrpslSeq, [2523]).subscribe({
      next: (res: any[]) => {

        if (!res?.length) {
          this.items = [];
          this.conclusion = '';
          this.conclusionBlkSeq = null;
          return;
        }

        this.items = [];
        this.conclusion = '';
        this.conclusionBlkSeq = null;

        // ✅ backend already crdtPrpslSeq ke hisaab se filtered data deta hai,
        // is liye Math.max wali latestProposalId calculation ki ab zaroorat nahi
        bySortOrder(res).forEach((row: any) => {
          if (row.sortOrdr === 999) {
            this.conclusion = readParagraphBlockText(row);
            this.conclusionBlkSeq = row.crdtPrpslBlkSeq ?? null;
          } else {
            const risk = row.jsonData?.[0] || {};

            this.items.push({
              // Risks saved in the old bullet mode (one point per line) open
              // as an editor bullet list, and are saved as rich text from then on.
              content: risk.showBullet
                ? bulletLinesToRichText(risk.bullets?.length ? risk.bullets : risk.content)
                : risk.content || '',
              expanded: false,
              crdtPrpslBlkSeq: row.crdtPrpslBlkSeq ?? null,
              delFlg: row.delFlg ?? 0,
            });
          }
        });
      },
      error: (err) => {
        console.error(err);
      },
    });
  }

  saveRiskSection(): void {
    // At least one risk is required; a conclusion alone does not complete the section.
    if (!this.activeItems.some((item) => hasRichTextContent(item.content))) {
      const message = this.translate.instant('CREDIT_SECTION_RISK.RISK_REQUIRED');

      this.toast.error(message);

      this.creditService.reportSectionSaveResult({
        sectionId: 'key-risks',
        status: 'validation-error',
        message,
      });

      return;
    }

    const payload = {
      crprtOnbrdngSeq: this.onboardingId,
      crdtPrpslSeq: this.crdtPrpslSeq, // ✅ shared seq

      blockList: [
        ...this.items.map((item, index) => ({
          crdtPrpslBlkSeq: item.crdtPrpslBlkSeq ?? null,

          refGrp: 28,
          refValSeq: 2523,

          blkTyp: 2,
          delFlg: item.delFlg ?? 0,

          sortOrdr: index + 1,

          txtData: '',

          // Bullets now live inside the rich text (editor lists), so the old
          // bullet-mode fields are always saved off/empty.
          jsonData: [
            {
              showBullet: false,
              bullets: [],
              content: richTextForSave(item.content),
            },
          ],
        })),

        {
          crdtPrpslBlkSeq: this.conclusionBlkSeq ?? null,

          refGrp: 28,
          refValSeq: 2523,

          blkTyp: 1,
          delFlg: 0,

          sortOrdr: 999,

          ...paragraphBlockData(this.conclusion),
        },
      ],
    };

    this.creditService.saveSection(payload).subscribe({
      next: (res: any) => {
        // ✅ shared service mein propagate karo
        this.creditService.setCrdtPrpslSeq(res?.crdtPrpslSeq);
        this.loadRiskSection();

        this.creditService.reportSectionSaveResult({
          sectionId: 'key-risks',
          status: 'saved',
        });

        this.toast.success('CREDIT_SECTION_RISK.SAVED');
      },
      error: (err) => {
        console.error(err);

        this.creditService.reportSectionSaveResult({
          sectionId: 'key-risks',
          status: 'api-error',
          message: err?.error?.message ?? this.translate.instant('CREDIT_SECTION_RISK.SAVE_FAILED'),
        });
      },
    });
  }
}